// CompoundNode.jsx - Nodo espandibile con canvas interno per tabelloni di eliminazione
import React, { useState, useCallback, useMemo, useRef, useEffect, memo } from 'react';
import ReactDOM from 'react-dom';
import MatchNode from './MatchNode';
import InternalConnection from './InternalConnection';
import { TEMPLATE_OPTIONS, applyTemplate, calculateBracketDimensions } from './BracketTemplates';
import './CompoundNode.css';

const FASCIA_OPTIONS = [
  { value: 1, label: 'Fascia 1' },
  { value: 2, label: 'Fascia 2' },
  { value: 3, label: 'Fascia 3' },
  { value: 4, label: 'Fascia 4' }
];

const CompoundNode = ({
  node,
  selected,
  onSelect,
  onDoubleClick,
  onDelete,
  onStartConnection,
  onEndConnection,
  onHover,
  onLeave,
  onUpdateNode,
  connecting,
  scale = 1 // solo la scala, non il transform intero: cosi' il pan non lo ridisegna
}) => {
  // Stato interno
  const [expanded, setExpanded] = useState(node.expanded ?? true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [internalConnecting, setInternalConnecting] = useState(null);
  const [selectedInternalConnection, setSelectedInternalConnection] = useState(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showFasciaDropdown, setShowFasciaDropdown] = useState(false);
  const [fasciaDropdownPos, setFasciaDropdownPos] = useState({ x: 0, y: 0 });
  const fasciaBtnRef = useRef(null);

  // Drag state per match interni
  const [draggingMatch, setDraggingMatch] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dropdownPos, setDropdownPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const templateBtnRef = useRef(null);

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    if (!showTemplateDropdown) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.template-dropdown') || e.target.closest('.toolbar-btn-template')) return;
      setShowTemplateDropdown(false);
    };
    setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTemplateDropdown]);

  // Chiudi dropdown fascia quando si clicca fuori
  useEffect(() => {
    if (!showFasciaDropdown) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.fascia-dropdown') || e.target.closest('.fascia-btn')) return;
      setShowFasciaDropdown(false);
    };
    setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFasciaDropdown]);

  // Handler per cambio fascia
  const handleFasciaChange = (fasciaValue) => {
    onUpdateNode?.(node.id, { fascia: fasciaValue });
    setShowFasciaDropdown(false);
  };

  const currentFascia = node.fascia || 1;

  // Dati interni del tabellone
  const matches = node.internalMatches || [];
  const internalConnections = node.internalConnections || [];
  const inputMapping = node.inputMapping || [];
  const outputMapping = node.outputMapping || [];

  // Dimensioni
  const HEADER_HEIGHT = 30;
  const TOOLBAR_HEIGHT = 32;
  const CONTENT_PADDING = 10;
  const MIN_WIDTH = 350;
  const MIN_HEIGHT = 200;

  // Calcola dimensioni dinamiche quando espanso
  const dimensions = useMemo(() => {
    if (!expanded) {
      return { width: 150, height: 80 };
    }
    const bracketDims = calculateBracketDimensions(matches);
    return {
      width: Math.max(bracketDims.width + CONTENT_PADDING * 2 + 40, MIN_WIDTH),
      height: Math.max(bracketDims.height + HEADER_HEIGHT + TOOLBAR_HEIGHT + CONTENT_PADDING * 2 + 20, MIN_HEIGHT)
    };
  }, [expanded, matches]);

  // Configurazione colori
  const colors = {
    fill: expanded ? '#FFF8E1' : '#FF9800',
    stroke: selected ? '#1976D2' : '#E65100',
    header: '#E65100',
    toolbar: '#FFE0B2',
    headerText: 'white'
  };

  // Offset per contenuto interno (area dei match)
  const contentOffset = useMemo(() => ({
    x: node.x + CONTENT_PADDING,
    y: node.y + HEADER_HEIGHT + TOOLBAR_HEIGHT + CONTENT_PADDING
  }), [node.x, node.y]);

  // Area interna disponibile
  const internalArea = useMemo(() => ({
    width: dimensions.width - CONTENT_PADDING * 2,
    height: dimensions.height - HEADER_HEIGHT - TOOLBAR_HEIGHT - CONTENT_PADDING * 2
  }), [dimensions]);

  // === HANDLERS NODO PRINCIPALE ===

  const isInternalContent = (element) => {
    let current = element;
    while (current && current.tagName !== 'svg') {
      if (current.classList && (
        current.classList.contains('internal-content') ||
        current.classList.contains('compound-toolbar') ||
        current.classList.contains('match-node')
      )) {
        return true;
      }
      current = current.parentElement || current.parentNode;
    }
    return false;
  };

  const handleMouseDown = (e) => {
    // Solo il tasto sinistro: il centrale deve arrivare al canvas, che con quello fa il pan.
    if (e.button !== 0) return;
    if (isInternalContent(e.target)) return;
    e.stopPropagation();
    onSelect?.(node, e);
  };

  const handleMouseEnter = () => onHover?.(node.id);
  const handleMouseLeave = () => onLeave?.();

  // === HANDLERS TOOLBAR ===

  const toggleExpanded = (e) => {
    e.stopPropagation();
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onUpdateNode?.(node.id, { expanded: newExpanded });
  };

  const addMatch = (e) => {
    e.stopPropagation();
    const newMatch = {
      id: `match-${Date.now()}`,
      name: `Match ${matches.length + 1}`,
      x: 20 + (matches.length % 3) * 140,
      y: 20 + Math.floor(matches.length / 3) * 80
    };
    onUpdateNode?.(node.id, {
      internalMatches: [...matches, newMatch]
    });
  };

  const handleApplyTemplate = (templateKey) => {
    if (!templateKey) return;

    const template = applyTemplate(templateKey);
    const dims = calculateBracketDimensions(template.matches);

    // Sostituisce completamente lo schema esistente
    onUpdateNode?.(node.id, {
      internalMatches: template.matches,
      internalConnections: template.connections,
      inputMapping: template.inputMapping,
      outputMapping: template.outputMapping,
      expanded: true,
      width: dims.width + 80,
      height: dims.height + HEADER_HEIGHT + TOOLBAR_HEIGHT + 40
    });

    setShowTemplateDropdown(false);
  };

  // === HANDLERS MATCH INTERNI ===

  const handleMatchSelect = useCallback((match, e) => {
    e.stopPropagation();
    setSelectedMatch(match.id === selectedMatch ? null : match.id);
    setSelectedInternalConnection(null);
  }, [selectedMatch]);

  const handleMatchDelete = useCallback((matchId) => {
    const updatedMatches = matches.filter(m => m.id !== matchId);
    const updatedConnections = internalConnections.filter(
      c => c.fromMatchId !== matchId && c.toMatchId !== matchId
    );
    const updatedInputMapping = inputMapping.filter(m => m.matchId !== matchId);
    const updatedOutputMapping = outputMapping.filter(m => m.matchId !== matchId);

    onUpdateNode?.(node.id, {
      internalMatches: updatedMatches,
      internalConnections: updatedConnections,
      inputMapping: updatedInputMapping,
      outputMapping: updatedOutputMapping
    });
    setSelectedMatch(null);
  }, [matches, internalConnections, inputMapping, outputMapping, node.id, onUpdateNode]);

  const handleMatchUpdateName = useCallback((matchId, newName) => {
    const updatedMatches = matches.map(m =>
      m.id === matchId ? { ...m, name: newName } : m
    );
    onUpdateNode?.(node.id, { internalMatches: updatedMatches });
  }, [matches, node.id, onUpdateNode]);

  // === DRAG & DROP MATCH INTERNI ===

  const handleMatchDragStart = useCallback((matchId, e) => {
    e.stopPropagation();
    const match = matches.find(m => m.id === matchId);
    if (!match) return;

    // Salva l'offset dal punto di click alla posizione del match
    setDraggingMatch(matchId);
    setDragOffset({
      x: e.clientX,
      y: e.clientY,
      matchX: match.x,
      matchY: match.y
    });
  }, [matches]);

  const handleMatchDrag = useCallback((e) => {
    if (!draggingMatch || !dragOffset.x) return;
    e.stopPropagation();

    // la scala arriva come prop

    // Calcola quanto si è spostato il mouse dal punto iniziale
    const deltaX = (e.clientX - dragOffset.x) / scale;
    const deltaY = (e.clientY - dragOffset.y) / scale;

    // Nuova posizione = posizione iniziale + delta
    let newX = dragOffset.matchX + deltaX;
    let newY = dragOffset.matchY + deltaY;

    // Limita ai bounds dell'area interna
    newX = Math.max(0, Math.min(newX, internalArea.width - MatchNode.WIDTH));
    newY = Math.max(0, Math.min(newY, internalArea.height - MatchNode.HEIGHT));

    // Aggiorna posizione match
    const updatedMatches = matches.map(m =>
      m.id === draggingMatch ? { ...m, x: newX, y: newY } : m
    );
    onUpdateNode?.(node.id, { internalMatches: updatedMatches });
  }, [draggingMatch, dragOffset, matches, scale, internalArea, node.id, onUpdateNode]);

  const handleMatchDragEnd = useCallback(() => {
    setDraggingMatch(null);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  // === HANDLERS CONNESSIONI INTERNE ===

  const handleStartInternalConnection = useCallback((matchId, portId, portType) => {
    setInternalConnecting({ matchId, portId, portType });
  }, []);

  const handleEndInternalConnection = useCallback((matchId, portId, portType) => {
    if (!internalConnecting) return;

    if (internalConnecting.portType === 'output' && portType === 'input') {
      const newConnection = {
        id: `conn-${Date.now()}`,
        fromMatchId: internalConnecting.matchId,
        fromPort: internalConnecting.portId,
        toMatchId: matchId,
        toPort: portId
      };

      const exists = internalConnections.some(
        c => c.fromMatchId === newConnection.fromMatchId &&
             c.fromPort === newConnection.fromPort &&
             c.toMatchId === newConnection.toMatchId &&
             c.toPort === newConnection.toPort
      );

      if (!exists) {
        onUpdateNode?.(node.id, {
          internalConnections: [...internalConnections, newConnection]
        });
      }
    }

    setInternalConnecting(null);
  }, [internalConnecting, internalConnections, node.id, onUpdateNode]);

  const handleDeleteInternalConnection = useCallback((connectionId) => {
    const updatedConnections = internalConnections.filter(c => c.id !== connectionId);
    onUpdateNode?.(node.id, { internalConnections: updatedConnections });
    setSelectedInternalConnection(null);
  }, [internalConnections, node.id, onUpdateNode]);

  // === HANDLERS CONNESSIONI ESTERNE ===

  const handleExternalPortClick = (e, portIndex, portType) => {
    e.stopPropagation();
    e.preventDefault();

    if (connecting) {
      onEndConnection?.(node.id, portIndex, portType);
    } else {
      onStartConnection?.(node.id, portIndex, portType);
    }
  };

  // === CALCOLO PORTE ESTERNE ===

  // Calcola posizioni porte input basandosi sui match collegati
  const inputPortPositions = useMemo(() => {
    return inputMapping.map((mapping) => {
      const targetMatch = matches.find(m => m.id === mapping.matchId);
      if (!targetMatch) {
        return { ...mapping, x: node.x - 8, y: node.y + 100, targetX: null, targetY: null };
      }

      // Calcola la Y della porta del match interno
      const portYRatio = mapping.portId === 'in-1' ? 0.3 : 0.7;
      const matchAbsY = contentOffset.y + targetMatch.y;
      const matchPortY = matchAbsY + MatchNode.HEIGHT * portYRatio;
      const matchPortX = contentOffset.x + targetMatch.x - 6; // Porta input del match

      return {
        ...mapping,
        x: node.x - 8,
        y: matchPortY,
        targetX: matchPortX,
        targetY: matchPortY
      };
    });
  }, [inputMapping, matches, node.x, node.y, contentOffset]);

  const outputPortPositions = useMemo(() => {
    const count = outputMapping.length || 1;
    const startY = node.y + HEADER_HEIGHT + TOOLBAR_HEIGHT;
    const availableHeight = dimensions.height - HEADER_HEIGHT - TOOLBAR_HEIGHT;
    const spacing = availableHeight / (count + 1);
    return outputMapping.map((mapping, i) => ({
      ...mapping,
      x: node.x + dimensions.width + 8,
      y: startY + spacing * (i + 1)
    }));
  }, [outputMapping, dimensions.height, dimensions.width, node.x, node.y]);

  // === RENDER ===

  return (
    <g
      ref={containerRef}
      className={`compound-node ${selected ? 'selected' : ''} ${expanded ? 'expanded' : 'collapsed'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={(e) => {
        handleMouseLeave();
        if (draggingMatch) handleMatchDragEnd();
      }}
      onMouseMove={draggingMatch ? handleMatchDrag : undefined}
      onMouseUp={draggingMatch ? handleMatchDragEnd : undefined}
    >
      {/* Sfondo principale */}
      <rect
        x={node.x}
        y={node.y}
        width={dimensions.width}
        height={dimensions.height}
        fill={colors.fill}
        stroke={colors.stroke}
        strokeWidth={selected ? 3 : 1.5}
        rx={8}
        onMouseDown={handleMouseDown}
        style={{ cursor: 'grab' }}
      />

      {/* Header */}
      <rect
        x={node.x}
        y={node.y}
        width={dimensions.width}
        height={HEADER_HEIGHT}
        fill={colors.header}
        rx={8}
        onMouseDown={handleMouseDown}
        style={{ cursor: 'grab' }}
      />
      <rect
        x={node.x}
        y={node.y + HEADER_HEIGHT - 8}
        width={dimensions.width}
        height={8}
        fill={colors.header}
        onMouseDown={handleMouseDown}
        style={{ cursor: 'grab' }}
      />

      {/* Titolo header con dropdown fascia */}
      <foreignObject
        x={node.x + 8}
        y={node.y + 4}
        width={dimensions.width - 50}
        height={HEADER_HEIGHT - 8}
        style={{ overflow: 'visible' }}
      >
        <div
          className="compound-header-title"
          xmlns="http://www.w3.org/1999/xhtml"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            height: '100%',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold'
          }}
        >
          <span>Dirette:</span>
          <button
            ref={fasciaBtnRef}
            className="fascia-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (!showFasciaDropdown && fasciaBtnRef.current) {
                const rect = fasciaBtnRef.current.getBoundingClientRect();
                setFasciaDropdownPos({ x: rect.left, y: rect.bottom + 2 });
              }
              setShowFasciaDropdown(!showFasciaDropdown);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: '4px',
              color: 'white',
              padding: '2px 8px',
              fontSize: '11px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            Fascia {currentFascia} ▾
          </button>
        </div>
      </foreignObject>

      {/* Pulsante espandi/comprimi */}
      <g
        className="expand-button"
        onClick={toggleExpanded}
        style={{ cursor: 'pointer' }}
      >
        <rect
          x={node.x + dimensions.width - 28}
          y={node.y + 5}
          width={20}
          height={20}
          fill="rgba(255,255,255,0.2)"
          rx={3}
        />
        <text
          x={node.x + dimensions.width - 18}
          y={node.y + 19}
          textAnchor="middle"
          fill="white"
          fontSize="14"
          fontWeight="bold"
          pointerEvents="none"
        >
          {expanded ? '−' : '+'}
        </text>
      </g>

      {/* Contenuto espanso */}
      {expanded && (
        <>
          {/* Toolbar interna */}
          <rect
            x={node.x}
            y={node.y + HEADER_HEIGHT}
            width={dimensions.width}
            height={TOOLBAR_HEIGHT}
            fill={colors.toolbar}
          />

          {/* Toolbar con foreignObject per elementi HTML */}
          <foreignObject
            x={node.x + 8}
            y={node.y + HEADER_HEIGHT + 4}
            width={dimensions.width - 16}
            height={TOOLBAR_HEIGHT - 8}
            style={{ overflow: 'visible' }}
          >
            <div
              className="compound-toolbar"
              xmlns="http://www.w3.org/1999/xhtml"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                className="toolbar-btn toolbar-btn-add"
                onClick={addMatch}
                title="Aggiungi partita"
              >
                + Match
              </button>

              <div className="toolbar-separator-v" />

              <div className="template-dropdown-container">
                <button
                  ref={templateBtnRef}
                  className="toolbar-btn toolbar-btn-template"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!showTemplateDropdown && templateBtnRef.current) {
                      const rect = templateBtnRef.current.getBoundingClientRect();
                      setDropdownPos({ x: rect.left, y: rect.bottom + 2 });
                    }
                    setShowTemplateDropdown(!showTemplateDropdown);
                  }}
                  title="Applica template (sostituisce lo schema esistente)"
                >
                  Template ▾
                </button>
              </div>

              <div className="toolbar-info">
                {matches.length} match • {internalConnections.length} conn.
              </div>
            </div>
          </foreignObject>

          {/* Area contenuto interno */}
          <g className="internal-content">
            {/* Sfondo area di lavoro */}
            <rect
              x={node.x + CONTENT_PADDING}
              y={node.y + HEADER_HEIGHT + TOOLBAR_HEIGHT + CONTENT_PADDING}
              width={internalArea.width}
              height={internalArea.height}
              fill="rgba(255,255,255,0.6)"
              stroke="#E0E0E0"
              strokeWidth="1"
              strokeDasharray="4,4"
              rx={4}
            />

            {/* Connessioni interne */}
            {internalConnections.map(conn => (
              <InternalConnection
                key={conn.id}
                connection={conn}
                matches={matches}
                parentOffset={contentOffset}
                onDelete={handleDeleteInternalConnection}
                selected={selectedInternalConnection === conn.id}
                onSelect={(c) => {
                  setSelectedInternalConnection(c.id);
                  setSelectedMatch(null);
                }}
              />
            ))}

            {/* Match interni */}
            {matches.map(match => (
              <MatchNode
                key={match.id}
                match={match}
                selected={selectedMatch === match.id}
                onSelect={handleMatchSelect}
                onDelete={handleMatchDelete}
                onUpdateName={handleMatchUpdateName}
                fascia={currentFascia}
                onStartConnection={handleStartInternalConnection}
                onEndConnection={handleEndInternalConnection}
                onDragStart={handleMatchDragStart}
                connecting={internalConnecting?.matchId}
                connectingPort={internalConnecting}
                parentOffset={contentOffset}
                scale={scale}
                isDragging={draggingMatch === match.id}
              />
            ))}

            {/* Placeholder quando vuoto */}
            {matches.length === 0 && (
              <text
                x={node.x + dimensions.width / 2}
                y={node.y + HEADER_HEIGHT + TOOLBAR_HEIGHT + internalArea.height / 2}
                textAnchor="middle"
                fill="#999"
                fontSize="12"
                pointerEvents="none"
              >
                Clicca "+ Match" o seleziona un Template
              </text>
            )}
          </g>
        </>
      )}

      {/* Info quando compresso */}
      {!expanded && (
        <g>
          <text
            x={node.x + dimensions.width / 2}
            y={node.y + 50}
            textAnchor="middle"
            fill="white"
            fontSize="11"
            pointerEvents="none"
          >
            {matches.length} match
          </text>
          <text
            x={node.x + dimensions.width / 2}
            y={node.y + 66}
            textAnchor="middle"
            fill="rgba(255,255,255,0.7)"
            fontSize="9"
            pointerEvents="none"
          >
            [+] per espandere
          </text>
        </g>
      )}

      {/* Porte Input esterne (sinistra) con linee di collegamento */}
      {expanded && inputPortPositions.map((port, index) => (
        <g key={`input-${index}`}>
          {/* Linea di collegamento dalla porta esterna alla porta del match */}
          {port.targetX && (
            <path
              d={`M ${port.x + 6} ${port.y}
                  C ${port.x + 30} ${port.y},
                    ${port.targetX - 20} ${port.targetY},
                    ${port.targetX} ${port.targetY}`}
              fill="none"
              stroke="#2196F3"
              strokeWidth="2"
              strokeDasharray="4,2"
              opacity="0.6"
              pointerEvents="none"
            />
          )}
          {/* Porta esterna */}
          <circle
            cx={port.x}
            cy={port.y}
            r="6"
            fill="#2196F3"
            stroke={colors.header}
            strokeWidth="2"
            onClick={(e) => handleExternalPortClick(e, index, 'input')}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ cursor: 'crosshair' }}
          />
        </g>
      ))}

      {/* Porte Output esterne (destra) */}
      {expanded && outputPortPositions.map((port, index) => (
        <g key={`output-${index}`}>
          <text
            x={node.x + dimensions.width - 8}
            y={port.y + 3}
            textAnchor="end"
            fill="#666"
            fontSize="9"
            pointerEvents="none"
          >
            {port.label || `Out ${index + 1}`}
          </text>
          <circle
            cx={port.x}
            cy={port.y}
            r="6"
            fill={port.portId === 'winner' ? '#4CAF50' : port.portId === 'loser' ? '#f44336' : '#FFF'}
            stroke={colors.header}
            strokeWidth="2"
            onClick={(e) => handleExternalPortClick(e, index, 'output')}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ cursor: 'crosshair' }}
          />
        </g>
      ))}

      {/* Indicatore connessione attiva */}
      {connecting === node.id && (
        <rect
          x={node.x - 4}
          y={node.y - 4}
          width={dimensions.width + 8}
          height={dimensions.height + 8}
          fill="none"
          stroke="#2196F3"
          strokeWidth="2"
          strokeDasharray="5,5"
          rx={10}
          pointerEvents="none"
        />
      )}

      {/* Dropdown Template - renderizzato come portal fuori dall'SVG */}
      {showTemplateDropdown && ReactDOM.createPortal(
        <div
          className="template-dropdown"
          style={{
            position: 'fixed',
            left: dropdownPos.x,
            top: dropdownPos.y,
            zIndex: 99999
          }}
        >
          {TEMPLATE_OPTIONS.filter(opt => opt.teams > 0).map(opt => (
            <div
              key={opt.value}
              className="template-option"
              onClick={() => handleApplyTemplate(opt.value)}
            >
              <span className="template-name">{opt.teams} squadre</span>
            </div>
          ))}
        </div>,
        document.body
      )}

      {/* Dropdown Fascia - renderizzato come portal fuori dall'SVG */}
      {showFasciaDropdown && ReactDOM.createPortal(
        <div
          className="fascia-dropdown"
          style={{
            position: 'fixed',
            left: fasciaDropdownPos.x,
            top: fasciaDropdownPos.y,
            zIndex: 99999,
            background: 'white',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid #E0E0E0',
            minWidth: '100px',
            overflow: 'hidden'
          }}
        >
          {FASCIA_OPTIONS.map(opt => (
            <div
              key={opt.value}
              className="fascia-option"
              onClick={() => handleFasciaChange(opt.value)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#333',
                background: currentFascia === opt.value ? '#FFF3E0' : 'transparent',
                borderLeft: currentFascia === opt.value ? '3px solid #E65100' : '3px solid transparent'
              }}
              onMouseEnter={(e) => e.target.style.background = '#FFF8E1'}
              onMouseLeave={(e) => e.target.style.background = currentFascia === opt.value ? '#FFF3E0' : 'transparent'}
            >
              {opt.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </g>
  );
};

CompoundNode.HEADER_HEIGHT = 30;
CompoundNode.TOOLBAR_HEIGHT = 32;
CompoundNode.MIN_WIDTH = 350;
CompoundNode.MIN_HEIGHT = 200;

export default memo(CompoundNode);
